import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Search, Users, Shield, Building2, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import api from '../services/api';
import { cn } from '../lib/utils';

function timeAgo(iso) {
  if (!iso) return '—';
  const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function RoleBadge({ role }) {
  const styles = {
    superadmin: 'bg-red-500/10 text-red-400 border-red-500/20',
    admin: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    user: 'bg-zinc-800 text-zinc-400 border-zinc-700',
  };
  return (
    <span className={cn('text-xs px-2 py-0.5 rounded border font-medium', styles[role] || styles.user)}>
      {role}
    </span>
  );
}

export function AllUsers() {
  const [users, setUsers] = useState([]);
  const [workspaces, setWorkspaces] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const baseURL = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
      const token = localStorage.getItem('line_rich_menu_token');
      const authHeader = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
      // Use raw axios (no workspace header) to get ALL users
      const [usersRes, wsRes] = await Promise.all([
        axios.get(`${baseURL}/users`, { headers: authHeader }),
        api.get('/workspaces'),
      ]);
      setUsers(usersRes.data.users || []);
      const wsMap = {};
      (wsRes.data.workspaces || []).forEach(ws => { wsMap[ws.id] = ws.name; });
      setWorkspaces(wsMap);
    } catch (e) {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = users.filter(u => {
    const q = search.toLowerCase();
    return !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
  });

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">All Users</h1>
          <p className="text-[#A1A1AA] text-sm mt-1">
            {loading ? '…' : `${users.length} total`}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={load}
          disabled={loading}
          className="border-[#27272A]"
        >
          <RefreshCw className={cn('w-4 h-4', loading && 'animate-spin')} />
        </Button>
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
        <Input
          placeholder="Search by name or email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="pl-9 bg-[#18181B] border-[#27272A] text-zinc-100 placeholder:text-[#52525B]"
        />
      </div>

      <div className="bg-[#18181B] border border-[#27272A] rounded-lg overflow-hidden">
        {loading ? (
          <div className="divide-y divide-[#27272A]">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="px-5 py-4 flex items-center gap-4">
                <div className="w-8 h-8 rounded-full bg-[#27272A] animate-pulse" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-40 bg-[#27272A] rounded animate-pulse" />
                  <div className="h-3 w-56 bg-[#27272A] rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : !filtered.length ? (
          <div className="px-5 py-12 text-center text-[#52525B]">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">{search ? 'No users match your search' : 'No users found'}</p>
          </div>
        ) : (
          <div className="divide-y divide-[#27272A]">
            {filtered.map(u => {
              const memberships = u.workspaceMemberships || [];
              return (
                <div key={u.id} className="px-5 py-4 flex items-start gap-4">
                  {/* Avatar */}
                  <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-emerald-400 text-xs font-semibold">
                      {u.name?.[0]?.toUpperCase() || '?'}
                    </span>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-zinc-100 truncate">{u.name}</span>
                      <RoleBadge role={u.role} />
                    </div>
                    <p className="text-xs text-[#71717A] mt-0.5 truncate">{u.email}</p>

                    {/* Workspace memberships */}
                    {memberships.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {memberships.map(m => (
                          <span
                            key={m.workspaceId}
                            className="inline-flex items-center gap-1 text-xs bg-[#27272A] text-[#A1A1AA] px-2 py-0.5 rounded border border-[#3F3F46]"
                          >
                            <Building2 className="w-3 h-3" />
                            {workspaces[m.workspaceId] || m.workspaceId}
                            {m.role === 'admin' && (
                              <Shield className="w-3 h-3 text-amber-400" />
                            )}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-[#52525B] mt-1.5 italic">No workspace</p>
                    )}
                  </div>

                  {/* Joined */}
                  <div className="text-xs text-[#52525B] flex-shrink-0 flex items-center gap-1 mt-0.5">
                    <Calendar className="w-3 h-3" />
                    {timeAgo(u.createdAt)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default AllUsers;
